import {
  Box,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { StatusChip } from "components/StatusChip";
import { Link } from "react-router-dom";

import { drawerSectionSx, DrawerSectionHeading } from "./DrawerSection";
import {
  correctionFieldLabel,
  correctionPathLabel,
  correctionStateLabel,
  correctionValueLabel,
  formatLongDay,
  isCorrectionClosedUnattributed,
  isCorrectionLanded,
  ManifestCorrectionRow,
} from "./manifestCorrectionDisplay";

type Props = {
  corrections: ManifestCorrectionRow[];
  /** Builds the per-row manifest link-outs. */
  tenantId: string;
};

/**
 * The manifest corrections staff cited against this exceedance — the fixes that
 * account for it once resolved, or the ones it is parked on until they land.
 * Renders nothing unless the exceedance carries a MANIFEST_CORRECTION resolution,
 * which is the only way links exist.
 *
 * A cited correction that closes unattributed reopens the exceedance and drops its
 * links (`reconcileExceedancesOnDiscrepancyClose`), so that state is transient here
 * rather than something to warn about; the chip still reports it truthfully in the
 * window before the pipeline reconciles.
 */
export function ManifestCorrectionsTable({ corrections, tenantId }: Props) {
  if (corrections.length === 0) return null;

  return (
    <Box sx={drawerSectionSx}>
      <DrawerSectionHeading>Manifest corrections</DrawerSectionHeading>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Manifest</TableCell>
            <TableCell>Field</TableCell>
            <TableCell>Corrected to</TableCell>
            <TableCell>State</TableCell>
            <TableCell>Corrected in</TableCell>
            <TableCell>Corrected</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {corrections.map((c) => (
            <TableRow key={c.id}>
              <TableCell>
                <MuiLink
                  component={Link}
                  to={`/o/${tenantId}/waste/manifests/${c.processedManifestId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {c.manifestTrackingNumber}
                </MuiLink>
                {c.shippedDate && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    shipped {formatLongDay(c.shippedDate)}
                  </Typography>
                )}
              </TableCell>
              <TableCell>{correctionFieldLabel(c.field)}</TableCell>
              <TableCell>{correctionValueLabel(c.eManifestValue)}</TableCell>
              <TableCell>
                <StatusChip color={correctionChipColor(c)} label={correctionStateLabel(c)} />
              </TableCell>
              <TableCell>{correctionPathLabel(c.resolutionPath)}</TableCell>
              <TableCell sx={{ color: "text.secondary" }}>
                {c.resolvedAt ? formatLongDay(c.resolvedAt) : "—"}
                {c.resolvedByName ? ` · ${c.resolvedByName}` : ""}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

/** Landed reads as done, an unattributed close as a problem, in flight as neutral. */
function correctionChipColor(row: ManifestCorrectionRow): "success" | "warning" | "info" {
  if (isCorrectionLanded(row)) return "success";
  if (isCorrectionClosedUnattributed(row)) return "warning";
  return "info";
}
